import { asInstanceOf, isDefined, UUID } from "@opendaw/lib-std";
import { WclapDeviceBox, WclapInstrumentBox, WclapParameterBox } from "@opendaw/studio-boxes";
// existing boxes keep their value and links, the host is the source of truth
export var WclapParameters;
(function (WclapParameters) {
    WclapParameters.reconcile = (project, uuid, params) => {
        const hub = parametersOf(project, uuid);
        if (!isDefined(hub)) {
            return;
        }
        const wanted = new Map(params.filter(isAutomatable).map(param => [param.id, param]));
        outsideHistory(project, () => {
            const existing = new Map();
            for (const { box } of hub.pointerHub.incoming()) {
                const paramBox = asInstanceOf(box, WclapParameterBox);
                const id = paramBox.clapId.getValue() >>> 0;
                const info = wanted.get(id);
                if (!isDefined(info)) {
                    paramBox.delete();
                    continue;
                }
                existing.set(id, paramBox);
                paramBox.label.setValue(info.name);
                paramBox.module.setValue(info.module);
                paramBox.flags.setValue(info.flags);
                // each bound write rebuilds the adapter's mapping, so the range must never invert in between
                if (info.min >= paramBox.max.getValue()) {
                    paramBox.max.setValue(info.max);
                    paramBox.min.setValue(info.min);
                }
                else {
                    paramBox.min.setValue(info.min);
                    paramBox.max.setValue(info.max);
                }
                paramBox.defaultValue.setValue(info.defaultValue);
            }
            for (const info of wanted.values()) {
                if (existing.has(info.id)) {
                    continue;
                }
                WclapParameterBox.create(project.boxGraph, parameterUuid(hub.box.address.uuid, info.id), paramBox => {
                    paramBox.owner.refer(hub);
                    paramBox.label.setValue(info.name);
                    paramBox.module.setValue(info.module);
                    paramBox.clapId.setValue(info.id | 0);
                    paramBox.min.setValue(info.min);
                    paramBox.max.setValue(info.max);
                    paramBox.flags.setValue(info.flags);
                    paramBox.defaultValue.setValue(info.defaultValue);
                    paramBox.value.setValue(info.value);
                });
            }
        });
    };
    // The parameter boxes mirror what the loaded plugin reports, so they stay out of the undo history: an undo
    // would delete them while the plugin stays loaded, and it reports them again only when it loads again.
    // They reach the engine and a live room like any other change of the graph.
    const outsideHistory = (project, exec) => {
        const graph = project.boxGraph;
        if (graph.inTransaction()) {
            exec();
            return;
        }
        graph.beginTransaction();
        exec();
        graph.endTransaction();
    };
    WclapParameters.apply = (project, uuid, paramId, value, gesture) => {
        if (gesture !== 0) {
            return;
        }
        const hub = parametersOf(project, uuid);
        if (!isDefined(hub)) {
            return;
        }
        const paramBox = hub.pointerHub.incoming()
            .map(({ box }) => asInstanceOf(box, WclapParameterBox))
            .find(paramBox => (paramBox.clapId.getValue() >>> 0) === paramId);
        if (!isDefined(paramBox) || paramBox.value.getValue() === Math.fround(value)) {
            return;
        }
        // through the adapter like a knob or MIDI learn, so automation recording and suspension see the write
        const adapter = project.parameterFieldAdapters.opt(paramBox.value.address);
        project.editing.modify(() => adapter.match({
            none: () => paramBox.value.setValue(value),
            some: adapter => adapter.setValue(value)
        }), false);
    };
    const IS_HIDDEN = 1 << 2;
    const IS_READONLY = 1 << 3;
    const isAutomatable = ({ flags }) => (flags & (IS_HIDDEN | IS_READONLY)) === 0;
    // every client derives the same uuid, so engines reporting at once in a live room create one box. The last
    // byte is always flipped, else clap id 0 would hand out the device's own uuid.
    const parameterUuid = (deviceUuid, clapId) => {
        const bytes = new Uint8Array(deviceUuid);
        new DataView(bytes.buffer).setUint32(0, new DataView(bytes.buffer).getUint32(0) ^ clapId);
        bytes[15] ^= 0xA5;
        return bytes;
    };
    const parametersOf = (project, uuid) => {
        const box = project.boxGraph.findBox(UUID.parse(uuid)).unwrapOrNull();
        if (box instanceof WclapDeviceBox || box instanceof WclapInstrumentBox) {
            return box.parameters;
        }
        return undefined;
    };
})(WclapParameters || (WclapParameters = {}));
